import { useEffect } from 'react';
import { useAppDispatch } from '@/redux/Store';
import { addMessage } from '@/redux/slices/chatSlice';
import type { ChatMessage } from '@/redux/slices/chatSlice';
import { useSocket } from '@/contexts/SocketContext';

export function useChatSocket(activeRoomId: string | null) {
  const dispatch = useAppDispatch();
  const { socket } = useSocket();

  // Join / leave room when activeRoomId changes
  useEffect(() => {
    if (!socket || !activeRoomId) return;
    socket.emit('join-chat-room', activeRoomId);
    return () => {
      socket.emit('leave-chat-room', activeRoomId);
    };
  }, [socket, activeRoomId]);

  // Listen for incoming messages. Megaphones (including the legacy
  // 'megaphone' event for cross-version safety) arrive as normal
  // messages — the slice mirrors them into the aggregation room when
  // isMegaphone is true. Cross-room global-megaphone banners are
  // listened for in ChatRoom directly so the banner state stays local
  // to whichever room is rendering it.
  useEffect(() => {
    if (!socket) return;

    const onMessage = (msg: ChatMessage) => {
      dispatch(addMessage(msg));
    };

    socket.on('new-chat-message', onMessage);
    socket.on('megaphone', onMessage);

    return () => {
      socket.off('new-chat-message', onMessage);
      socket.off('megaphone', onMessage);
    };
  }, [socket, dispatch]);
}
